/*
  Voegt dubbele winkelwagenregels van dezelfde variant samen tot één regel.

  Waarom: de Bogos-widget (vaak samen gekocht) plakt verborgen
  _bogos_trigger-eigenschappen op producten die via de widget worden
  toegevoegd. Shopify voegt regels alleen samen als ze exact gelijk zijn,
  dus een gewone toevoeging plus een widget-toevoeging van hetzelfde
  product levert twee losse regels op. Bogos-support kan dit niet
  uitschakelen (gevraagd op 20 augustus 2026), daarom dit vangnet.

  Veiligheidsregels:
  - Het gratis cadeau (url bevat sca_clone_freegift) blijft altijd staan.
  - Alleen regels waarvan het enige verschil de _bogos_-labels zijn worden
    samengevoegd; echte verschillen (opties, gravure, abonnement) nooit.
  - De regel mét Bogos-labels wint, zodat de Bogos-attributie op de regel
    behouden blijft en de app niet in de war raakt.
  - Staan er twee of meer regels mét Bogos-labels in een groep (bundel twee
    keer toegevoegd), dan blijven die allemaal staan: aan die labels hangt
    de bundelkorting (order #5146: €555), en samenvoegen kan die korting
    breken. Alleen een schone dubbele regel wordt in de bundelregel
    opgenomen; volle prijs voor het extra stuk klopt dan gewoon.
  - Alle regels in één /cart/update.js-aanroep, zodat er geen tussentoestand
    bestaat waarin een deel van de wijziging wel en een deel niet is gelukt.

  Staat bewust los van cart-drawer.js: dat bestand is standaard Dawn en
  blijft onaangeraakt.
*/
(() => {
  'use strict';

  // Locale-bewuste basis (bijv. /en/), anders rendert de refresh op de
  // Engelse of Duitse site ineens Nederlands.
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Eigen verzoeken lopen via de originele fetch, zodat de interceptor
  // hieronder ze niet ziet en zichzelf niet opnieuw aanslingert.
  const origineleFetch = window.fetch.bind(window);

  let bezig = false;
  let opnieuw = false;
  let timer = null;

  const isCadeau = (item) => (item.url || '').includes('sca_clone_freegift');

  const heeftBogosLabels = (item) =>
    Object.keys(item.properties || {}).some((k) => k.startsWith('_bogos_'));

  // Eigenschappen zonder de _bogos_-labels, als stabiele sleutel.
  const schoneProps = (item) =>
    JSON.stringify(
      Object.entries(item.properties || {})
        .filter(([k]) => !k.startsWith('_bogos_'))
        .sort()
    );

  const groepsSleutel = (item) =>
    [
      item.variant_id,
      item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : '',
      schoneProps(item),
    ].join('|');

  async function ververs() {
    const res = await origineleFetch(root + '?sections=cart-drawer,cart-icon-bubble');
    if (!res.ok) return;
    const sections = await res.json();
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

    const drawer = document.querySelector('#CartDrawer');
    if (drawer && sections['cart-drawer']) {
      const vers = parse(sections['cart-drawer']).querySelector('#CartDrawer');
      if (vers) drawer.innerHTML = vers.innerHTML;
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const vers = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (vers) bubble.innerHTML = vers.innerHTML;
    }
  }

  async function voegSamen() {
    if (bezig) {
      // Kwam er tijdens het samenvoegen een nieuwe wijziging binnen, dan
      // draaien we daarna nog een ronde in plaats van hem te missen.
      opnieuw = true;
      return;
    }
    bezig = true;
    try {
      const cartRes = await origineleFetch(root + 'cart.js');
      if (!cartRes.ok) return;
      const cart = await cartRes.json();

      const groepen = new Map();
      for (const item of cart.items) {
        if (isCadeau(item)) continue;
        const sleutel = groepsSleutel(item);
        if (!groepen.has(sleutel)) groepen.set(sleutel, []);
        groepen.get(sleutel).push(item);
      }

      const updates = {};
      for (const items of groepen.values()) {
        if (items.length < 2) continue;

        // Bundel twee keer toegevoegd: beide regels dragen Bogos-labels en
        // dus mogelijk elk hun eigen korting. Daar blijven we vanaf.
        const gelabeld = items.filter(heeftBogosLabels);
        if (gelabeld.length >= 2) continue;

        const winnaar = gelabeld[0] || items[0];
        const totaal = items.reduce((som, i) => som + i.quantity, 0);
        for (const item of items) {
          updates[item.key] = item.key === winnaar.key ? totaal : 0;
        }
      }
      if (Object.keys(updates).length === 0) return;

      const res = await origineleFetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates }),
      });
      if (!res.ok) return;

      await ververs();
    } catch (e) {
      // Bij een fout niets forceren; de klant kan altijd nog zelf plussen.
    } finally {
      bezig = false;
      if (opnieuw) {
        opnieuw = false;
        plan();
      }
    }
  }

  function plan() {
    clearTimeout(timer);
    timer = setTimeout(voegSamen, 400);
  }

  // Elke toevoeging of wijziging — van het thema óf van een app — loopt
  // via fetch of XHR naar /cart/add, /cart/change of /cart/update.
  const cartUrl = /\/cart\/(add|change|update)(\.js)?/;

  window.fetch = function (...args) {
    const belofte = origineleFetch(...args);
    try {
      // Dekt string, URL-object en Request af.
      const url = args[0] instanceof Request ? args[0].url : String(args[0]);
      if (cartUrl.test(url)) belofte.then(plan).catch(() => {});
    } catch (e) {
      /* nooit de echte fetch hinderen */
    }
    return belofte;
  };

  const origineleOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (methode, url, ...rest) {
    try {
      if (cartUrl.test(String(url))) {
        this.addEventListener('loadend', plan);
      }
    } catch (e) {
      /* idem */
    }
    return origineleOpen.call(this, methode, url, ...rest);
  };

  // Vangnet voor toevoegingen die de interceptors niet zien (een app die
  // een eigen kopie van fetch vasthield van vóór dit script): telkens als
  // de drawer opengaat, controleren we de cart alsnog.
  const drawerEl = document.querySelector('cart-drawer');
  if (drawerEl) {
    let stondOpen = false;
    new MutationObserver(() => {
      const open = drawerEl.classList.contains('active');
      if (open && !stondOpen) plan();
      stondOpen = open;
    }).observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
  }

  // Eén controle bij het laden van de pagina, voor het geval de dubbele
  // regels al in de cart stonden (bijv. via een gewone formulier-submit).
  // Bij een aantoonbaar lege cart slaan we de check over, zodat niet elke
  // bezoeker voor niets /cart.js aanroept.
  function eersteControle() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('is-empty')) return;
    if (!drawer && !document.cookie.includes('cart=')) return;
    plan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', eersteControle);
  } else {
    eersteControle();
  }
})();